import React, { useCallback, useEffect, useState } from 'react'
import { CheckIcon } from '@heroicons/react/24/outline'
import { formatToleranceInput } from '../../../utils/pqr/weldParamsWpsLimits'

interface WeldParametersToleranceBarProps {
  tolerance: string
  onToleranceChange: (next: string) => void
  onSave: () => void | Promise<void>
  busy?: boolean
  dirty?: boolean
  /** When true, render inline for the page header toolbar (no card chrome). */
  compact?: boolean
}

/**
 * Per-weld tolerance control for the Welding Parameters page.
 * Mirrors the Data360 Production PQR tolerance input; default is 0%.
 */
export default function WeldParametersToleranceBar({
  tolerance,
  onToleranceChange,
  onSave,
  busy = false,
  dirty = false,
  compact = false
}: WeldParametersToleranceBarProps) {
  const [local, setLocal] = useState(tolerance)

  useEffect(() => {
    setLocal(tolerance)
  }, [tolerance])

  const handleBlur = useCallback(() => {
    const trimmed = local.trim()
    if (!trimmed) {
      onToleranceChange(formatToleranceInput(0))
      setLocal(formatToleranceInput(0))
      return
    }
    const normalized = trimmed.endsWith('%') ? trimmed : `${trimmed.replace(/%/g, '')}%`
    onToleranceChange(normalized)
    setLocal(normalized)
  }, [local, onToleranceChange])

  return (
    <div
      className={
        compact
          ? 'flex flex-wrap items-center gap-2'
          : 'flex flex-wrap items-center gap-3 rounded-lg border border-[#E2E5EA] bg-white px-4 py-3 shadow-sm'
      }
      data-testid="wp-tolerance-bar"
    >
      <div className="flex items-center gap-2">
        <span className="text-xs font-semibold text-text-grey">Tolerance</span>
        <input
          type="text"
          value={local}
          onChange={(e) => {
            const next = e.target.value
            setLocal(next)
            onToleranceChange(next)
          }}
          onBlur={handleBlur}
          className={
            compact
              ? 'h-8 w-16 rounded-md border border-[#E2E5EA] px-2 text-center text-xs text-text-grey outline-none transition-colors focus:border-hero-red focus:ring-2 focus:ring-hero-red/30'
              : 'h-9 w-20 rounded-md border border-[#E2E5EA] px-3 text-center text-[13px] text-text-grey outline-none transition-colors focus:border-hero-red focus:ring-2 focus:ring-hero-red/30'
          }
          placeholder="0%"
          title="Tolerance percentage (MinOfMin / MaxOfMax)"
          data-testid="wp-tolerance-input"
          disabled={busy}
        />
      </div>
      {!compact ? (
        <span className="text-[11px] text-heritage-grey">Limits: MinOfMin / MaxOfMax</span>
      ) : null}
      <button
        type="button"
        onClick={() => void onSave()}
        disabled={busy || !dirty}
        data-testid="wp-tolerance-save"
        className={
          compact
            ? 'inline-flex items-center gap-1 rounded-md border border-primary-200 bg-primary-50 px-2.5 py-1.5 text-[11px] font-semibold text-primary-700 shadow-sm transition-colors hover:bg-primary-100 disabled:cursor-not-allowed disabled:opacity-50'
            : 'inline-flex items-center gap-1.5 rounded-md border border-primary-200 bg-primary-50 px-3 py-1.5 text-xs font-semibold text-primary-700 shadow-sm transition-colors hover:bg-primary-100 disabled:cursor-not-allowed disabled:opacity-50'
        }
      >
        <CheckIcon className={compact ? 'h-3 w-3' : 'h-3.5 w-3.5'} />
        {busy ? 'Saving…' : 'Save'}
      </button>
    </div>
  )
}
